"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuthFlow } from "../../auth-flow-context";
import { AuthMethodId } from "../../types";
import { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cx } from "@/lib/tailwind-utils";
import { CodeEntry } from "./code-entry";
import { NameCompletion } from "./name-completion";
import { PatientPicker } from "./patient-picker";
import { PhoneAuthContext, PhoneAuthStep, usePhoneAuth } from "./phone-auth-context";
import { PhoneEntry } from "./phone-entry";
import { TAndSFooter, TAndSFooterProps } from "./t-and-s-footer";
import { usePhoneAuthProvider } from "./use-phone-auth";
export interface PhoneMethodProps {
  children?: ReactNode;
  className?: string;
}
function PhoneAuthSkeleton(t0) {
  const $ = _c(4);
  const {
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-full space-y-2 text-center"><Skeleton className="mx-auto h-7 w-56" /><Skeleton className="mx-auto h-4 w-72" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-full space-y-4"><div className="space-y-2"><Skeleton className="h-4 w-28" /><Skeleton className="h-10 w-full" /></div><Skeleton className="h-10 w-full" /></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <div className="flex w-full flex-col gap-6" role="status" aria-live="polite" aria-label={label}>{t1}{t2}</div>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Owns the phone-auth state machine and publishes it via
 * `PhoneAuthContext`. No UI of its own — pair with `<AuthFlow.Phone>`
 * for the standard form, or read `usePhoneAuth()` from sibling
 * components (dialog shells, alternate auth columns, etc.) when the
 * surface needs to react to the current step.
 *
 * Must live inside `<AuthFlow>` (it consumes the flow's `runAuth` and
 * `onSuccess`).
 */
function PhoneProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const value = usePhoneAuthProvider();
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <PhoneAuthContext.Provider value={value}>{children}</PhoneAuthContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Renders the phone-auth UI. Requires `<AuthFlow.Phone.Provider>`
 * above it; no provider, no render. Hides itself when the flow's
 * active method isn't PHONE.
 */
export function PhoneMethod(t0) {
  const $ = _c(8);
  const {
    className,
    children
  } = t0;
  const {
    state: flowState
  } = useAuthFlow();
  const {
    state
  } = usePhoneAuth();
  if (flowState.activeMethod !== AuthMethodId.PHONE) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cx("px-12 py-8", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== state.isResolvingSession) {
    t2 = state.isResolvingSession ? <PhoneAuthSkeleton label={"Welcome back\u2026"} /> : children ?? <><PhoneEntry className="space-y-6" /><CodeEntry className="space-y-6" /><PatientPicker className="space-y-6" /><NameCompletion className="space-y-6" /></>;
    $[2] = children;
    $[3] = state.isResolvingSession;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * Terms-and-conditions footer scoped to the phone-entry step.
 */
function PhoneTAndS(props) {
  const $ = _c(2);
  const {
    state
  } = usePhoneAuth();
  if (state.step !== PhoneAuthStep.ENTER_PHONE) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <TAndSFooter {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export interface PhoneWhenStepProps {
  step: PhoneAuthStep | ReadonlyArray<PhoneAuthStep>;
  children: ReactNode;
}

/**
 * Renders children only when the lifted phone-auth step matches.
 * Use anywhere inside `<AuthFlow.Phone.Provider>` to gate sibling UI.
 */
function PhoneWhenStep(t0) {
  const $ = _c(2);
  const {
    step,
    children
  } = t0;
  const {
    state
  } = usePhoneAuth();
  const steps = Array.isArray(step) ? step : [step as PhoneAuthStep];
  if (!steps.includes(state.step)) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
PhoneMethod.Provider = PhoneProvider;
PhoneMethod.Entry = PhoneEntry;
PhoneMethod.Code = CodeEntry;
PhoneMethod.PickPatient = PatientPicker;
PhoneMethod.Name = NameCompletion;
PhoneMethod.TAndS = PhoneTAndS;
PhoneMethod.WhenStep = PhoneWhenStep;